import { MagicConfigMixin } from '@gig/magic-common';
import { __extends, __awaiter, __generator, __decorate, __metadata } from 'tslib';
import { Ref, Component, Mixins } from 'vue-property-decorator';
import { magic } from '@gig/magic-sdk';
import { USER_PROFILE_DATA } from '@gig/magic-messages';
import { notificationService, NotificationDelay } from '@gig/magic-messaging';
import { PhoneVerificationEvents } from './phone-verification.constants.js';

// Constants
var DEFAULT_SMS_CODE_LENGTH = 6;
var RESEND_COOLDOWN_SECONDS = 60;
var FOCUS_DELAY_MS = 100;
/**
 * Phone Verification Component
 *
 * Handles the phone verification flow for users with pending verification status.
 * - Allows users to request a verification code to be sent to their phone
 * - Provides a UI for entering and submitting the code
 * - Handles verification success/failure and appropriate notifications
 */
var PhoneVerificationComponent = /** @class */ (function (_super) {
    __extends(PhoneVerificationComponent, _super);
    function PhoneVerificationComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.code = '';
        _this.invalidCode = false;
        _this.isLoading = false;
        _this.hasCodeBeenSent = false;
        _this.phoneNumber = '';
        _this.resendDisabled = false;
        _this.resendCooldownSeconds = RESEND_COOLDOWN_SECONDS;
        _this.resendCooldownTimer = null;
        _this.smsCodeLength = DEFAULT_SMS_CODE_LENGTH;
        return _this;
    }
    Object.defineProperty(PhoneVerificationComponent.prototype, "isCodeValid", {
        get: function () {
            return this.code.length === this.smsCodeLength;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PhoneVerificationComponent.prototype, "userId", {
        get: function () {
            var user = magic.session.getUser();
            return (user === null || user === void 0 ? void 0 : user.id) || '';
        },
        enumerable: false,
        configurable: true
    });
    PhoneVerificationComponent.prototype.mounted = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.fetchUserProfile()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    PhoneVerificationComponent.prototype.beforeDestroy = function () {
        this.clearResendCooldownTimer();
    };
    PhoneVerificationComponent.prototype.handleInput = function (event) {
        var value = event.detail[0];
        this.code = value.replace(/\D/g, '');
        this.invalidCode = false;
    };
    PhoneVerificationComponent.prototype.resetError = function () {
        this.invalidCode = false;
    };
    PhoneVerificationComponent.prototype.fetchUserProfile = function () {
        return __awaiter(this, void 0, void 0, function () {
            var profile, userInfo, phoneDetails, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, window.queryBus.query(USER_PROFILE_DATA)];
                    case 1:
                        profile = _a.sent();
                        if (!profile || !profile.user) {
                            this.handleMissingPhoneNumber('Invalid user profile response');
                            return [2 /*return*/];
                        }
                        userInfo = profile.user;
                        phoneDetails = userInfo.mobileNumber || userInfo.telephoneNumber;
                        if (!phoneDetails || !phoneDetails.number || !phoneDetails.prefix) {
                            this.handleMissingPhoneNumber('Phone number not found in user profile');
                            return [2 /*return*/];
                        }
                        this.phoneNumber = "" + phoneDetails.prefix + phoneDetails.number;
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        console.error('Error fetching user profile:', error_1);
                        this.showErrorNotification('Failed to load user profile.');
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    PhoneVerificationComponent.prototype.handleMissingPhoneNumber = function (message) {
        this.showErrorNotification(message);
    };
    PhoneVerificationComponent.prototype.verifyPhoneCode = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response, error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.isCodeValid) {
                            this.showErrorNotification(this.$t('phone-verification.invalid-code-length'));
                            return [2 /*return*/];
                        }
                        this.isLoading = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, this.callVerificationApi()];
                    case 2:
                        response = _a.sent();
                        if (response.verified) {
                            this.handleSuccessfulVerification();
                        }
                        else {
                            this.handleFailedVerification();
                        }
                        return [3 /*break*/, 5];
                    case 3:
                        error_2 = _a.sent();
                        this.handleFailedVerification();
                        console.error('Error verifying code:', error_2);
                        return [3 /*break*/, 5];
                    case 4:
                        this.isLoading = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    PhoneVerificationComponent.prototype.callVerificationApi = function () {
        return __awaiter(this, void 0, void 0, function () {
            var userId;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        userId = this.userId;
                        return [4 /*yield*/, magic.transport.request('corex/verify-user-profile', {
                                userId: userId,
                                type: 'phone',
                                code: this.code,
                            })];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    PhoneVerificationComponent.prototype.handleSuccessfulVerification = function () {
        notificationService.success(this.$t('phone-verification.success'), { closeDelay: NotificationDelay.PERMANENT });
        this.$emit(PhoneVerificationEvents.CLOSE);
    };
    PhoneVerificationComponent.prototype.handleFailedVerification = function () {
        this.invalidCode = true;
        this.showErrorNotification(this.$t('phone-verification.error'));
    };
    PhoneVerificationComponent.prototype.sendVerificationCode = function () {
        return __awaiter(this, void 0, void 0, function () {
            var success, error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.phoneNumber) {
                            this.showErrorNotification(this.$t('phone-verification.phone-not-found'));
                            return [2 /*return*/];
                        }
                        if (this.resendDisabled) {
                            return [2 /*return*/];
                        }
                        this.isLoading = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, this.callSendCodeApi()];
                    case 2:
                        success = _a.sent();
                        if (success) {
                            this.handleCodeSentSuccess();
                        }
                        else {
                            this.handleCodeSendFailure();
                        }
                        return [3 /*break*/, 5];
                    case 3:
                        error_3 = _a.sent();
                        console.error('Error sending verification code:', error_3);
                        this.handleCodeSendFailure();
                        return [3 /*break*/, 5];
                    case 4:
                        this.isLoading = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    PhoneVerificationComponent.prototype.callSendCodeApi = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.userId) {
                            return [2 /*return*/, false];
                        }
                        return [4 /*yield*/, magic.methods.users.resendUserVerification(this.userId, 'phone')];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, true];
                }
            });
        });
    };
    PhoneVerificationComponent.prototype.handleCodeSentSuccess = function () {
        this.hasCodeBeenSent = true;
        this.startResendCooldown();
        notificationService.success(this.$t('phone-verification.code-sent'), { closeDelay: NotificationDelay.DEFAULT });
        this.focusInputAfterCodeSent();
    };
    PhoneVerificationComponent.prototype.handleCodeSendFailure = function () {
        this.showErrorNotification(this.$t('phone-verification.code-send-error'));
    };
    PhoneVerificationComponent.prototype.focusInputAfterCodeSent = function () {
        var _this = this;
        this.$nextTick(function () {
            setTimeout(function () {
                var _a;
                (_a = _this.codeInput) === null || _a === void 0 ? void 0 : _a.focus();
            }, FOCUS_DELAY_MS);
        });
    };
    PhoneVerificationComponent.prototype.startResendCooldown = function () {
        var _this = this;
        this.resendDisabled = true;
        this.clearResendCooldownTimer();
        this.resendCooldownSeconds = RESEND_COOLDOWN_SECONDS;
        this.resendCooldownTimer = window.setInterval(function () {
            _this.resendCooldownSeconds--;
            if (_this.resendCooldownSeconds <= 0) {
                _this.stopResendCooldown();
            }
        }, 1000);
    };
    PhoneVerificationComponent.prototype.stopResendCooldown = function () {
        this.clearResendCooldownTimer();
        this.resendDisabled = false;
    };
    PhoneVerificationComponent.prototype.clearResendCooldownTimer = function () {
        if (this.resendCooldownTimer) {
            window.clearInterval(this.resendCooldownTimer);
            this.resendCooldownTimer = null;
        }
    };
    PhoneVerificationComponent.prototype.showErrorNotification = function (message) {
        notificationService.error(message, {
            closeDelay: NotificationDelay.DEFAULT,
        });
    };
    PhoneVerificationComponent.prototype.onClose = function () {
        this.$emit(PhoneVerificationEvents.CLOSE);
        // If we're inside a magic-ui-modal, try to close it directly
        try {
            var closestModal = this.$el.closest('.modal');
            if (closestModal) {
                var closeEvent = new CustomEvent('close', { bubbles: true });
                closestModal.dispatchEvent(closeEvent);
            }
        }
        catch (e) {
            console.error('Error trying to close modal:', e);
        }
    };
    __decorate([
        Ref('codeInput'),
        __metadata("design:type", HTMLInputElement)
    ], PhoneVerificationComponent.prototype, "codeInput", void 0);
    PhoneVerificationComponent = __decorate([
        Component({
            name: 'phone-verification',
        })
    ], PhoneVerificationComponent);
    return PhoneVerificationComponent;
}(Mixins(MagicConfigMixin)));

export default PhoneVerificationComponent;
